import { Controller } from "react-hook-form";
import { Dropdown, Form } from "semantic-ui-react";

import type { DropdownItemProps } from "components";
import type { FieldValues, Path, PathValue } from "react-hook-form";
import type { DropdownProps, FormFieldProps } from "semantic-ui-react";

//type ShallowPath<T> = keyof T & string;

type TypeFormDropdown<T extends FieldValues, K extends Path<T>, V extends string | number | boolean> = {
  name: K;
  customSelectedValue?: string;
  dropdownProps?: DropdownProps;
  formFieldProps?: FormFieldProps;
  options: DropdownItemProps<V>[];
  onChange?: (data?: PathValue<T, K>) => void;
  parseOptionValueToFieldValue?: (data?: V) => PathValue<T, K>;
};

const isPathValue = <T extends FieldValues, K extends Path<T>>(value: unknown, target: PathValue<T, K> | undefined | null): value is PathValue<T, K> =>
  Array.isArray(value)
    ? Array.isArray(target) && (!target.length || !value.length || typeof target[0] === typeof value[0])
    : !value || !target || typeof value === typeof target;

const isParsableValue = <V extends string | number | boolean>(value: unknown, option: V | undefined): value is V => typeof value === typeof option;

export const FormDropdownHelper = <T extends FieldValues>() => {
  const FormDropdown = <V extends string | number | boolean, K extends Path<T> = Path<T>>({
    name,
    dropdownProps,
    formFieldProps,
    customSelectedValue,
    options,
    onChange,
    parseOptionValueToFieldValue
  }: TypeFormDropdown<T, K, V>) => (
    <Controller
      name={name}
      render={({ field, fieldState: { invalid, isTouched } }) => (
        <Form.Field {...formFieldProps} error={invalid && isTouched}>
          {!Array.isArray(field.value) && !dropdownProps?.multiple ? (
            <Dropdown
              value={field.value}
              text={customSelectedValue}
              onChange={(_, { value }) => {
                // TODO: currently we safeguard that the returned type of onChange is what the target type should be, but that's wrong
                // it should be the same type as what the dropdown option are, if you're trying to use string option on a form property that expect numbers
                // then you should get an error for you to parse the value, not tell the compiler you'll receive a number but on runtime receive nothing cause the safeguard blocked it
                // TODO: right now this this type the onChange value type as the field type but use a parsing function in case the option type is different than the field type
                // while this works, we don't know at compilation time if we must provide the parsing function or not, it will just not select anything at runtime if missed
                // find a way to solve this at compilation time
                const isSameType = isPathValue(value, field.value);
                const canParse = parseOptionValueToFieldValue && isParsableValue(value, options.at(0)?.value);

                if (value === undefined) field.onChange(undefined);
                else if (canParse) field.onChange(parseOptionValueToFieldValue(value));
                else if (isSameType) field.onChange(value);
                else console.error("parseOptionValueToFieldValue must be provided if the field type is not the same as the option type");

                if (onChange) {
                  if (value === undefined) onChange(undefined);
                  else if (canParse) onChange(parseOptionValueToFieldValue(value));
                  else if (isSameType) onChange(value);
                  else console.error("parseOptionValueToFieldValue must be provided if the field type is not the same as the option type");
                }
              }}
              onBlur={field.onBlur}
              selection
              options={options}
              {...dropdownProps}
            />
          ) : (
            <Dropdown
              multiple
              value={Array.isArray(field.value) ? field.value.filter((v: unknown) => !!v) : []}
              text={customSelectedValue}
              onChange={(_, { value }) => {
                field.onChange(value);
                if (onChange && isPathValue(value, field.value)) onChange(value);
              }}
              onBlur={field.onBlur}
              selection
              options={options}
              {...dropdownProps}
            />
          )}
        </Form.Field>
      )}
    />
  );

  return FormDropdown;
};
